import { useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { OrganisationListItem } from '@hospasense/contracts';
import { useOrganisations } from '../lib/queries.js';
import { useAuth } from '../lib/auth.js';
import { formatDateTime, relativeTime, titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  LifecyclePill,
  PageHeader,
  PermissionDeniedState,
  Skeleton,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui/index.js';
import {
  ColumnPicker,
  COLUMN_LABELS,
  OrgFilters,
  SavedViews,
  useColumnSelection,
  type ColumnKey,
} from './org-page-parts.js';

const SORTABLE: Partial<Record<ColumnKey, string>> = {
  displayName: 'displayName',
  legalName: 'legalName',
  lifecycleState: 'lifecycleState',
  jurisdiction: 'jurisdiction',
  updatedAt: 'updatedAt',
};

export function OrganisationsPage() {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const [columns, setColumns] = useColumnSelection();
  const navigate = useNavigate();
  const query = useOrganisations(params);

  const setParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  if (!hasPermission('organisation.read')) {
    return <PermissionDeniedState what="the Organisation Registry" />;
  }

  const sortBy = params.get('sortBy') ?? 'updatedAt';
  const sortDir = params.get('sortDir') ?? 'desc';
  const toggleSort = (field: string): void => {
    if (sortBy === field) setParam('sortDir', sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setParam('sortBy', field);
      setParam('sortDir', 'asc');
    }
  };

  const page = Number(params.get('page') ?? '1');
  const data = query.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organisations"
        subtitle="Synthetic customer registry — server-side search, filtering and sorting"
        breadcrumbs={
          <Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Organisations' }]} />
        }
        actions={
          hasPermission('organisation.create') ? (
            <div className="flex gap-2">
              {hasPermission('provisioning.dispatch') && (
                <Button
                  variant="secondary"
                  onClick={() => navigate('/organisations/new-test-hospital')}
                >
                  New test hospital
                </Button>
              )}
              <Button onClick={() => navigate('/organisations/new')}>New organisation</Button>
            </div>
          ) : undefined
        }
      />

      <div className="space-y-3 rounded-xl border border-border bg-surface p-4">
        <OrgFilters params={params} setParam={setParam} />
        <div className="flex flex-wrap items-end justify-between gap-3 border-t border-border pt-3">
          <SavedViews current={params} onApply={(s) => setParams(new URLSearchParams(s))} />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={params.get('includeArchived') === 'true'}
                onChange={(e) => setParam('includeArchived', e.target.checked ? 'true' : '')}
                className="size-4 accent-(--primary)"
              />
              Include archived
            </label>
            <ColumnPicker columns={columns} onChange={setColumns} />
          </div>
        </div>
      </div>

      {query.isLoading ? (
        <TableContainer>
          <Skeleton rows={8} />
        </TableContainer>
      ) : query.isError || !data ? (
        <ErrorState
          description="The organisation list could not be loaded."
          onRetry={() => void query.refetch()}
        />
      ) : data.items.length === 0 ? (
        <TableContainer>
          <EmptyState
            title="No organisations match"
            description="Organisations matching the current search and filters would appear here. Clear a filter or adjust the search."
            action={
              hasPermission('organisation.create') ? (
                <Button size="sm" onClick={() => navigate('/organisations/new')}>
                  New organisation
                </Button>
              ) : undefined
            }
          />
        </TableContainer>
      ) : (
        <>
          <TableContainer>
            <Table aria-label="Organisations" aria-rowcount={data.meta.totalItems}>
              <THead>
                <tr>
                  {columns.map((col) => {
                    const sortField = SORTABLE[col];
                    const active = sortField === sortBy;
                    return (
                      <TH
                        key={col}
                        aria-sort={
                          active ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                        }
                      >
                        {sortField ? (
                          <button
                            type="button"
                            onClick={() => toggleSort(sortField)}
                            className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-text-primary focus-visible:ring-2 focus-visible:ring-ring rounded-sm focus-visible:outline-none"
                          >
                            {COLUMN_LABELS[col]}
                            {active && (
                              <span aria-hidden="true">{sortDir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </button>
                        ) : (
                          COLUMN_LABELS[col]
                        )}
                      </TH>
                    );
                  })}
                </tr>
              </THead>
              <TBody>
                {data.items.map((org) => (
                  <TR
                    key={org.id}
                    interactive
                    onClick={() => navigate(`/organisations/${org.id}`)}
                    className={org.archivedAt ? 'bg-bg-subtle' : ''}
                  >
                    {columns.map((col) => (
                      <OrgCell key={col} col={col} org={org} />
                    ))}
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableContainer>

          <nav className="flex items-center justify-between text-sm" aria-label="Pagination">
            <p className="text-text-muted tabular-nums">
              {data.meta.totalItems} organisations · page {data.meta.page} of {data.meta.totalPages}
              <span className="ml-2 text-xs">fetched {relativeTime(data.meta.generatedAt)}</span>
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setParam('page', String(page - 1))}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= data.meta.totalPages}
                onClick={() => setParam('page', String(page + 1))}
              >
                Next
              </Button>
            </div>
          </nav>
        </>
      )}
    </div>
  );
}

function OrgCell({ col, org }: { col: ColumnKey; org: OrganisationListItem }) {
  switch (col) {
    case 'displayName':
      return (
        <TD>
          <Link
            to={`/organisations/${org.id}`}
            className="font-medium text-link hover:underline focus-visible:ring-2 focus-visible:ring-ring rounded-sm focus-visible:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            {org.displayName}
          </Link>
          {org.archivedAt && (
            <span className="ml-2">
              <Badge tone="neutral">Archived</Badge>
            </span>
          )}
        </TD>
      );
    case 'legalName':
      return (
        <TD className="max-w-52">
          <span className="block truncate" title={org.legalName}>
            {org.legalName}
          </span>
        </TD>
      );
    case 'organisationType':
      return <TD>{titleCase(org.organisationType)}</TD>;
    case 'deploymentType':
      return <TD>{org.deploymentType === 'on_prem' ? 'On-prem' : 'Cloud'}</TD>;
    case 'jurisdiction':
      return (
        <TD>
          <span className="font-mono text-xs">{org.jurisdiction}</span>
        </TD>
      );
    case 'lifecycleState':
      return (
        <TD>
          <LifecyclePill state={org.lifecycleState} />
        </TD>
      );
    case 'tags':
      return (
        <TD>
          <span className="flex flex-wrap gap-1">
            {org.tags.length === 0 ? (
              <span className="text-text-muted">—</span>
            ) : (
              org.tags.map((t) => (
                <Badge key={t} tone="accent">
                  {t}
                </Badge>
              ))
            )}
          </span>
        </TD>
      );
    case 'owners':
      return (
        <TD className="max-w-44">
          <span className="block truncate" title={org.owners.map((o) => o.displayName).join(', ')}>
            {org.owners.length === 0 ? (
              <span className="text-warning">Unowned</span>
            ) : (
              org.owners.map((o) => o.displayName).join(', ')
            )}
          </span>
        </TD>
      );
    case 'primaryContact':
      return (
        <TD>
          {org.primaryContactName ?? <span className="text-warning">None</span>}
          <span className="ml-1 text-xs text-text-muted tabular-nums">({org.contactCount})</span>
        </TD>
      );
    case 'updatedAt':
      return (
        <TD>
          <span title={formatDateTime(org.updatedAt)}>{relativeTime(org.updatedAt)}</span>
        </TD>
      );
  }
}
